import { useEffect, useState, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');
  const [error, setError] = useState<string | null>(null);

  // Clear payment in progress flag immediately on mount
  useLayoutEffect(() => {
    localStorage.removeItem('paymentInProgress');
  }, []);

  useEffect(() => {
    const verifyPayment = async () => {
      const razorpayPaymentId = searchParams.get('razorpay_payment_id');
      const razorpayOrderId = searchParams.get('razorpay_order_id');
      const razorpaySignature = searchParams.get('razorpay_signature');

      if (!razorpayPaymentId || !razorpayOrderId || !razorpaySignature) {
        setStatus('failed');
        setError('Missing payment details. Please contact support.');
        return;
      }

      // Get pending payment data from localStorage
      const pendingPaymentStr = localStorage.getItem('pendingPayment');
      if (!pendingPaymentStr) {
        setStatus('failed');
        setError('Payment session expired. Please contact support with payment ID: ' + razorpayPaymentId);
        return;
      }

      const pendingPayment = JSON.parse(pendingPaymentStr);

      try {
        // Verify the payment using express payment verification
        const { data: verificationResult, error: verifyError } = await supabase.functions.invoke('verify-express-payment', {
          body: {
            razorpay_payment_id: razorpayPaymentId,
            razorpay_order_id: razorpayOrderId,
            razorpay_signature: razorpaySignature,
            cartItems: pendingPayment.orderData.items,
            calculatedAmounts: {
              subtotal: pendingPayment.orderData.subtotal,
              shipping: pendingPayment.orderData.shipping,
              discount: pendingPayment.orderData.discount || 0,
              total: pendingPayment.orderData.total
            }
          }
        });

        if (verifyError) {
          throw new Error(verifyError.message || 'Payment verification failed');
        }

        if (verificationResult.success) {
          localStorage.removeItem('pendingPayment');
          
          sessionStorage.setItem('orderData', JSON.stringify({
            orderId: verificationResult.order.order_number || verificationResult.order.id,
            items: pendingPayment.orderData.items,
            subtotal: pendingPayment.orderData.subtotal,
            shipping: pendingPayment.orderData.shipping,
            discount: pendingPayment.orderData.discount || 0,
            total: pendingPayment.orderData.total,
            customerName: verificationResult.order.customer_name,
            createdAt: verificationResult.order.created_at,
            recovered: false
          }));

          setStatus('success');
          
          // Redirect to order success page after a brief delay
          setTimeout(() => {
            navigate('/order-success', { replace: true });
          }, 1500);
        } else {
          throw new Error('Payment verification failed');
        }
      } catch (err: any) {
        console.error('Payment verification error:', err);
        setStatus('failed');
        setError(err.message || 'Payment verification failed. Please contact support.');
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-card rounded-xl p-8 max-w-md w-full text-center shadow-lg border border-border">
        {status === 'verifying' && (
          <>
            <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">Verifying Payment</h1>
            <p className="text-muted-foreground">Please wait while we confirm your order...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground">Redirecting to your order confirmation...</p>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">Payment Issue</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/orders')} className="w-full">
                View My Orders
              </Button>
              <Button variant="outline" onClick={() => navigate('/cart')} className="w-full">
                Back to Cart
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
